<template>
  <div
    ref="timelineViewRef"
    class="timeline-view"
  >
    <div
      ref="timelineContainer"
      class="timeline-content"
    >
      <div
        v-if="showTimelineHeader"
        class="timeline-header"
      >
        <div
          class="header-tasks"
          :style="{
            width: `${timelineSidebarWidth}px`,
          }"
        >
          <ButtonCustom
            class="header-today-button"
            :disabled="positionOnStart"
            :text="translate('today')"
            @click="moveToStart"
          />
          <div class="timeline-zoom-dropdown">
            <ListDropdownWrap
              :dropdown-width="130"
              :height="32"
              :items="zoomOptions"
              :selected-item="selectedZoomIndex"
              :disable-scroll="true"
              @item-click="handleZoomOptionClick"
            />
          </div>
          <div class="timeline-zoom-buttons">
            <div
              v-tooltip="{
                content: translate('zoomIn'),
                placement: 'bottom',
              }"
              class="zoom-button rounded-tl rounded-bl pt"
              :class="{ 'disabled-zoom': zoomInDisabled }"
              @click="zoomInHandler"
            >
              <em
                :style="getCustomIconStyle(iconPath('plus'))"
                class="custom-icons"
              />
            </div>
            <div
              v-tooltip="{
                content: translate('zoomOut'),
                placement: 'bottom',
              }"
              class="zoom-button rounded-tr rounded-br -ml-px pt"
              :class="{ 'disabled-zoom': zoomOutDisabled }"
              @click="zoomOutHandler"
            >
              <em
                :style="getCustomIconStyle(iconPath('minus'))"
                class="custom-icons"
              />
            </div>
          </div>
        </div>
        <div
          class="header-content"
          :style="{
            width: `${containerWidth}px`,
          }"
        >
          <div
            class="header-content-items usn"
            :style="{
              transform: `translateX(${-position}px)`,
            }"
          >
            <div
              v-for="(item, index) in months"
              :key="`month-${index}`"
              class="month-item header-month-wrap"
              :style="{
                left: `${item.left}px`,
              }"
            >
              <span
                v-if="item.year"
                class="year-label"
              >
                {{ item.year }}
              </span>
              <span class="month-label">
                {{ item.name }}
              </span>
            </div>
            <div
              v-for="(item, index) in weeks"
              :key="`week-${index}`"
              :style="{ left: item.startLeft + 'px' }"
              class="day-item-wrap"
            >
              <span class="day-label">
                {{ item.startDayOfMonth }}
              </span>
            </div>
            <div
              class="today-highlight"
              :style="{ left: todayPosition + 'px' }"
            >
              <span class="today-label">
                {{ today.dayOfMonth }}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div
        class="vertical-lines-wrap"
        :style="{
          transform: `translateX(${-position}px)`,
        }"
      >
        <div
          v-for="(item, index) in timelineVerticalLines"
          :key="`vertical-line-${index}`"
          :style="{
            left: `${item.left}px`,
          }"
          class="vertical-line"
        />
      </div>
      <div
        :style="{
          width: `${timelineSidebarWidth}px`,
        }"
        class="timeline-sidebar-placeholder"
      />
      <BoardGridViewContent
        :position="position"
        :container-width="containerWidth"
      />
      <div
        v-if="todayLineStyle"
        class="today-line"
        :style="todayLineStyle"
      />
      <TimelineHorizontalScroll
        :scrolling-range="scrollingRange"
        :position="position"
        @scrollTo="moveTo"
        @scrollBy="moveBy"
      />
    </div>
    <div
      v-if="isResizerFocused"
      class="timeline-resize-overlay"
    />
    <div
      class="timeline-resizer rs usn"
      :class="[
        { 'is-focused': isResizerFocused },
        { 'default-width': timelineSidebarDefaultWidth },
      ]"
      :style="timelineResizerStyle"
      @mousedown="handleResizerMouseDown"
      @touchstart="handleResizerTouchStart"
    />
  </div>
</template>

<script>
import {
  getTimelineSidebarWidthLocal,
  getDefaultTimelineSidebarWidth,
  getMaxTimelineSidebarWidth,
  getMinTimelineSidebarWidth,
} from '@/utilities/rootStyles';
import ButtonCustom from '@/components/elements/ButtonCustom.vue';
import ListDropdownWrap from '@/components/widgets/ListDropdownWrap.vue';
import TimelineHorizontalScroll from '@/components/widgets/TimelineHorizontalScroll.vue';
import BoardGridViewContent from '@/components/widgets/BoardGridViewContent.vue';
import timelineMixin from '@/mixins/timelineMixin';
import timelineDragMixin from '@/mixins/timelineDragMixin';
import customIconsMixin from '@/mixins/customIconsMixin';
import addDays from 'date-fns/addDays';
import addMonths from 'date-fns/addMonths';
import startOfMonth from 'date-fns/startOfMonth';
import isBefore from 'date-fns/isBefore';
import getMonth from 'date-fns/getMonth';
import getYear from 'date-fns/getYear';
import startOfWeek from 'date-fns/startOfWeek';
import startOfDay from 'date-fns/startOfDay';
import addWeeks from 'date-fns/addWeeks';
import getDate from 'date-fns/getDate';
import compareAsc from 'date-fns/compareAsc';
import EventBus from '@/utilities/eventBus';
import isFirefox from '@/utilities/isFirefox';
import useBoardEmbed from '@/boards/composables/useBoardEmbed';

const tsDefaultWidth = getDefaultTimelineSidebarWidth();
const tsMaxWidth = getMaxTimelineSidebarWidth();
const tsMinWidth = getMinTimelineSidebarWidth();

export default {
  name: 'TimelineView',
  components: {
    ButtonCustom,
    ListDropdownWrap,
    TimelineHorizontalScroll,
    BoardGridViewContent,
  },
  mixins: [timelineMixin, timelineDragMixin, customIconsMixin],

  setup() {
    const { embeddedBoardAttributes, embed } = useBoardEmbed();

    return {
      embeddedBoardAttributes,
      embed,
    };
  },

  data() {
    return {
      isResizerFocused: false,
      timelineSidebarDefaultWidth: true,
      eventType: '',
      leftEdge: {
        date: new Date(),
        position: 0,
      },
      rightEdge: {
        date: new Date(),
        position: 0,
      },
      scrollingRange: {
        left: 0,
        right: 0,
      },
      position: 0,
      rawContainerWidth: 0,
      zoomOptions: [
        {
          id: 'year',
          label: this.translate('year'),
        },
        {
          id: 'quarter',
          label: this.translate('quarter'),
        },
        {
          id: 'month',
          label: this.translate('month'),
        },
      ],
      selectedZoomIndex: 0,
      animationCurrentValue: 0,
      animationRequestId: undefined,
      animatedMoveBy: null,
    };
  },
  computed: {
    timelineZoom: {
      get() {
        return this.$store.getters.getTimelineZoom;
      },
      set(value) {
        this.$store.dispatch('setTimelineZoom', value);
      },
    },
    monthsLabel() {
      return this.translate('months').split(',');
    },
    months() {
      const monthsArray = [];
      let leftEdgeDate = this.leftEdge.date;
      const rightEdgeDate = this.rightEdge.date;
      for (; isBefore(leftEdgeDate, rightEdgeDate); ) {
        const month = getMonth(leftEdgeDate);
        monthsArray.push({
          name: this.monthsLabel[month],
          year: month === 0 ? `${getYear(leftEdgeDate)}` : '',
          left: this.positionToLeft(this.dateToPosition(leftEdgeDate)),
        });
        leftEdgeDate = addMonths(leftEdgeDate, 1);
      }
      return monthsArray;
    },
    weeks() {
      const weeksArray = [];
      let leftEdgeDate = this.leftEdge.date;
      const rightEdgeDate = this.rightEdge.date;
      for (; isBefore(leftEdgeDate, rightEdgeDate); ) {
        const startWeek = startOfDay(startOfWeek(leftEdgeDate, { weekStartsOn: 1 }));
        leftEdgeDate = addWeeks(startWeek, 1);
        const endWeek = addDays(startWeek, 5);
        weeksArray.push({
          startDate: startWeek,
          startLeft: this.positionToLeft(this.dateToPosition(startWeek)),
          weekendStartLeft: this.positionToLeft(this.dateToPosition(endWeek)),
          endLeft: this.positionToLeft(this.dateToPosition(leftEdgeDate)),
          startDayOfMonth: getDate(startWeek),
        });
      }
      return weeksArray;
    },
    dayWidth() {
      switch (this.timelineZoom) {
        case 'year':
          return 5;
        case 'quarter':
          return 10;
        case 'month':
          return 20;
        default:
          return 1;
      }
    },
    timelineSidebarWidth() {
      return this.$store.getters.getTimelineSidebarWidth;
    },
    timelineResizerStyle() {
      return {
        left: `${this.timelineSidebarWidth - 4}px`,
      };
    },
    containerWidth() {
      return Math.max(0, this.rawContainerWidth - this.timelineSidebarWidth);
    },
    todayLineStyle() {
      return this.todayPosition + this.position > this.containerWidth
        ? null
        : {
            left: `${this.todayPosition + this.timelineSidebarWidth}px`,
            transform: `translateX(${-this.position}px)`,
          };
    },
    timelineVerticalLines() {
      switch (this.timelineZoom) {
        case 'year':
          return this.getYearLines();
        case 'quarter':
        case 'month':
          return this.getMonthLines();
        default:
          return [];
      }
    },
    edgeBoundary() {
      return 180 * this.dayWidth;
    },
    positionOnStart() {
      return this.position === 0;
    },
    chronologyWidth() {
      return this.rightEdge.position - this.leftEdge.position;
    },
    zoomOutDisabled() {
      return this.selectedZoomIndex === 0;
    },
    zoomInDisabled() {
      return this.selectedZoomIndex === this.zoomOptions.length - 1;
    },
    isSafariOrFirefox() {
      return isFirefox() || this.isSafariCheck;
    },
    showTimelineHeader() {
      return !(this.embed && this.embeddedBoardAttributes.sprintSidebar);
    },
  },
  watch: {
    timelineZoom(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.setZoom(newVal);
      }
    },
  },
  mounted() {
    this.setDefaultData();
    this.$refs.timelineViewRef.addEventListener('wheel', this.handleTimelineScroll, {
      passive: !this.isSafariOrFirefox,
    });
    this.animatedMoveBy = this.useAnimation((e) => {
      this.moveBy(e);
    });
    EventBus.$on('goToCard', this.moveTo);
  },
  created() {
    this.setTimelineSidebarWidthOnLoad();
    this.helplerElement = this.createHelperElement();
  },
  beforeUnmount() {
    this.removeResizerEvents();
    this.$refs.timelineViewRef?.removeEventListener?.('wheel', this.handleTimelineScroll, false);
  },
  unmounted() {
    document.body.removeChild(this.helplerElement);
    EventBus.$off('goToCard', this.moveTo);
    this.timelineContainerObserver?.disconnect();
  },
  methods: {
    handleResizerMouseDown() {
      this.isResizerFocused = true;
      this.eventType = 'mouse';
      document.addEventListener('mousemove', this.handleResizerMouseMove, false);
      document.addEventListener('mouseup', this.handleResizerMouseUp, false);
    },
    handleResizerTouchStart() {
      this.isResizerFocused = true;
      this.eventType = 'touch';
      document.addEventListener('touchmove', this.handleResizerTouchMove, false);
      document.addEventListener('touchend', this.handleResizerTouchUp, false);
    },
    handleResizerMouseMove(event) {
      if (!event) return;
      this.onResize(event.clientX);
    },
    handleResizerTouchMove(event) {
      if (!event) return;
      const touch = event.touches[0];
      this.onResize(touch.clientX);
    },
    handleResizerMouseUp() {
      this.isResizerFocused = false;
      this.removeResizerEvents();
      const localWidth = getTimelineSidebarWidthLocal();
      const currentWidth = this.timelineSidebarWidth;
      if (localWidth !== currentWidth) {
        this.setTimelineSidebarWidthLocal(currentWidth);
      }
    },
    handleResizerTouchUp() {
      this.isResizerFocused = false;
      this.removeResizerEvents();
    },
    removeResizerEvents() {
      const { eventType } = this;
      if (!eventType) return;

      if (eventType === 'mouse') {
        document.removeEventListener('mousemove', this.handleResizerMouseMove, false);
        document.removeEventListener('mouseup', this.handleResizerMouseUp, false);
      } else if (eventType === 'touch') {
        document.removeEventListener('touchmove', this.handleResizerTouchMove, false);
        document.removeEventListener('touchend', this.handleResizerTouchUp, false);
      }
      this.eventType = '';
    },
    onResize(clientX) {
      const { left: leftOffset } = this.$refs.timelineViewRef.getBoundingClientRect();
      let value = clientX - leftOffset;
      if (value < tsMinWidth) value = tsMinWidth;
      if (value > tsMaxWidth) value = tsMaxWidth;
      // check if new sidebar width is in range of snap width
      const { timelineSidebarResizeSnapWidth } = this.$constants;
      if (
        this.isInResizeRange(
          value,
          tsDefaultWidth - timelineSidebarResizeSnapWidth,
          tsDefaultWidth + timelineSidebarResizeSnapWidth
        )
      ) {
        // prevent unnecessary set of variable and function call
        if (this.timelineSidebarDefaultWidth) return;
        // set default width flag and remove root style
        this.timelineSidebarDefaultWidth = true;
        if (this.timelineSidebarWidth !== tsDefaultWidth) {
          this.$store.dispatch('setTimelineSidebarWidth', tsDefaultWidth);
        }
        return;
      }
      this.timelineSidebarDefaultWidth = false;
      this.$store.dispatch('setTimelineSidebarWidth', value);
    },
    isInResizeRange(width, minWidthResize, minWidth) {
      return width > minWidthResize && width < minWidth;
    },
    setTimelineSidebarWidthOnLoad() {
      const localWidth = getTimelineSidebarWidthLocal();
      if (localWidth !== tsDefaultWidth) {
        this.timelineSidebarDefaultWidth = false;
        this.$store.dispatch('setTimelineSidebarWidth', localWidth);
      }
    },
    setTimelineSidebarWidthLocal(value) {
      localStorage.setItem('timelineSidebarWidth', value);
    },
    setDefaultData() {
      const originDay = addDays(this.origin, Math.ceil(this.position / this.dayWidth));
      const leftEdgeDate = startOfMonth(addMonths(originDay, -12));
      this.leftEdge = {
        date: leftEdgeDate,
        position: this.dateToPosition(leftEdgeDate),
      };
      const rightEdgeDate = startOfMonth(addMonths(originDay, 13));
      this.rightEdge = {
        date: rightEdgeDate,
        position: this.dateToPosition(rightEdgeDate),
      };
      this.scrollingRange = {
        left: this.leftEdge.position,
        right: this.rightEdge.position,
      };
      this.timelineContainerObserver = new ResizeObserver(this.setRawContainerWidth).observe(
        this.$refs.timelineContainer
      );
      this.selectedZoomIndex = this.zoomOptions.findIndex((z) => z.id === this.timelineZoom);
    },
    setRawContainerWidth() {
      const { timelineContainer } = this.$refs;
      if (!timelineContainer) return;
      this.rawContainerWidth = timelineContainer.offsetWidth;
    },
    setZoom(newZoom) {
      const prevDayWidth = this.dayWidth;
      this.timelineZoom = newZoom;
      const zoomFactor = this.dayWidth / prevDayWidth;
      this.position = zoomFactor * this.position;
      this.leftEdge.position = zoomFactor * this.leftEdge.position;
      this.rightEdge.position = zoomFactor * this.rightEdge.position;
      this.scrollingRange.left = zoomFactor * this.scrollingRange.left;
      this.scrollingRange.right = zoomFactor * this.scrollingRange.right;
    },
    getYearLines() {
      return this.months.map((m, index) => {
        const monthPosition = m.left;
        return {
          id: index,
          left: monthPosition + this.timelineSidebarWidth,
        };
      });
    },
    getMonthLines() {
      return this.weeks.map((w) => {
        const weekPosition = w.weekendStartLeft;
        const lineWidth = w.endLeft - w.weekendStartLeft;
        return {
          width: lineWidth,
          left: weekPosition + this.timelineSidebarWidth,
        };
      });
    },
    updateChronology() {
      if (this.position > this.rightEdge.position - this.edgeBoundary) {
        const currentPositionDate = this.positionToDate(this.position);
        let e = startOfMonth(addMonths(this.rightEdge.date, 6));
        if (compareAsc(e, currentPositionDate) === -1) {
          e = startOfMonth(addMonths(currentPositionDate, 6));
        }
        this.rightEdge = {
          date: e,
          position: this.dateToPosition(e),
        };
      } else if (this.position < this.leftEdge.position + this.edgeBoundary) {
        const currentPositionDate = this.positionToDate(this.position);
        let e = startOfMonth(addMonths(this.leftEdge.date, -6));
        if (compareAsc(e, currentPositionDate) === 1) {
          e = startOfMonth(addMonths(currentPositionDate, -6));
        }
        this.leftEdge = {
          date: e,
          position: this.dateToPosition(e),
        };
      }
    },
    updateScrollingRange() {
      if (this.position < this.scrollingRange.left) {
        this.scrollingRange.left = this.position;
      }
      if (this.position > this.scrollingRange.right) {
        this.scrollingRange.right = this.position;
      }
    },
    moveBy(delta) {
      if (delta === 0) return;
      this.position += delta;
      this.updateChronology();
      this.updateScrollingRange();
    },
    moveTo(position) {
      this.position = position;
      this.updateChronology();
      this.updateScrollingRange();
    },
    moveToStart() {
      if (this.positionOnStart) return;
      this.moveTo(0);
    },
    handleZoomOptionClick(zoom, index) {
      this.selectedZoomIndex = index;
      this.setZoom(zoom.id);
    },
    handleTimelineScroll(e) {
      const { deltaX, deltaY, shiftKey } = e;
      if (deltaX === 0) {
        if (shiftKey) {
          this.animatedMoveBy(deltaY);
        }
        return;
      }
      const result = deltaY !== 0 ? deltaX / deltaY : 5;
      const absoluteValue = Math.abs(result);
      if (absoluteValue > 1) {
        this.animatedMoveBy(deltaX);
        if (this.isSafariOrFirefox) {
          e.stopPropagation();
          e.preventDefault();
        }
      }
    },
    accumulateValues(currentValue, newValue) {
      return currentValue + newValue;
    },
    useAnimation(callback, accumulate = this.accumulateValues) {
      return (
        (() => {
          return () => {
            if (this.animationRequestId) cancelAnimationFrame(this.animationRequestId);
          };
        },
        []),
        (r) => {
          this.animationCurrentValue = accumulate(this.animationCurrentValue, r);
          this.animationRequestId ||
            (this.animationRequestId = requestAnimationFrame(() => {
              callback(this.animationCurrentValue);
              this.animationCurrentValue = 0;
              this.animationRequestId = undefined;
            }));
        }
      );
    },
    createHelperElement() {
      const el = document.createElement('div');
      el.id = this.$constants.timelineHelperElementId;
      const style = {
        visibility: 'hidden',
        position: 'absolute',
        top: '-9999px',
        left: '-9999px',
      };
      Object.assign(el.style, style);
      document.body.appendChild(el);
      return el;
    },
    zoomInHandler() {
      if (this.selectedZoomIndex === this.zoomOptions.length - 1) return;
      this.selectedZoomIndex += 1;
      this.setZoom(this.zoomOptions[this.selectedZoomIndex].id);
    },
    zoomOutHandler() {
      if (this.selectedZoomIndex === 0) return;
      this.selectedZoomIndex -= 1;
      this.setZoom(this.zoomOptions[this.selectedZoomIndex].id);
    },
  },
};
</script>

<style lang="scss">
.timeline-view {
  @apply flex
    grow
    items-stretch
    h-full
    overflow-hidden
    relative;

  * {
    @apply box-border;
  }

  .timeline-content {
    @apply flex
      flex-col
      w-full
      h-full
      bg-background-strongest;

    > * {
      @apply overscroll-x-contain;
    }

    .timeline-header {
      border-bottom: 1px solid;
      @apply flex
        relative
        bg-background-strongest
        border-dividers-default;
      z-index: 3;

      .header-tasks {
        border-right: 1px solid;
        @apply flex
          bg-background-strongest
          px-4
          items-center
          box-border
          border-dividers-default;
        flex: 0 0 auto;
        height: 60px;
        z-index: 2;

        .timeline-zoom-dropdown {
          @apply ml-2;
          .dropdown-wrap {
            .dropdown-closed,
            .dropdown-opened {
              @apply px-[7px]
                py-[5px]
                bg-important-bg
                hover:bg-important-hover
                rounded
                border
                border-solid
                border-important-border
                gap-2;
              box-shadow: none;

              .custom-icons.dropdown-wrap-arrow-down {
                @apply min-w-[12px] min-h-[12px] w-3 h-3;
              }
            }
          }
        }

        .timeline-zoom-buttons {
          @apply ml-auto flex items-center;

          .zoom-button {
            @apply flex
              items-center
              justify-center
              w-6
              h-6
              bg-important-bg
              hover:bg-important-hover
              border
              border-solid
              border-important-border;

            &.disabled-zoom {
              @apply pointer-events-none;

              .custom-icons {
                @apply bg-text-placeholder #{!important};
              }
            }
          }
        }
      }

      .header-content {
        @apply relative;
        flex: 0 0 auto;

        .header-content-items {
          @apply absolute
            inset-0;

          .header-month-wrap {
            @apply absolute;
            padding: 6px 0px;

            .month-label,
            .year-label {
              @apply truncate
                text-left
                text-text-placeholder
                text-sm
                font-medium;
            }

            .year-label {
              @apply text-text-default;
            }
          }

          .day-item-wrap {
            @apply absolute
              leading-6;
            transform: translateX(-50%);
            bottom: 6px;

            .day-label {
              @apply truncate
                text-left
                text-text-placeholder
                text-sm
                font-medium;
            }
          }

          .today-highlight {
            @apply absolute
              flex
              justify-center
              items-center
              rounded-full
              w-6
              h-6;

            bottom: 6px;
            transform: translateX(-50%);
            background-color: $warning300;
            box-shadow: 0px 0px 8px 3px var(--shadowBackgroundStrongest);

            .today-label {
              @apply text-left
                text-gray-800
                text-sm
                font-medium
                select-none;
            }
          }
        }
      }
    }

    .vertical-lines-wrap {
      @apply absolute
        top-0
        bottom-0
        left-0;

      .vertical-line {
        @apply absolute
          top-0
          h-full
          w-0
          border
          border-dashed
          border-dividers-default;
      }
    }

    .timeline-sidebar-placeholder {
      border-right: 1px solid;
      @apply absolute
        top-0
        left-0
        bottom-0
        bg-background-strongest
        border-dividers-default;
    }

    .today-line {
      @apply absolute
        top-0
        bottom-0
        pointer-events-none;
      width: 2px;
      background: linear-gradient(0deg, #ffebbd 0%, $warning300 100%);
      opacity: 0.6;
      z-index: 1;
    }

    .board-view-wrap {
      .draggable-content {
        .draggable-cards {
          .list-footer {
            @apply m-0;
          }
          .board-item-list-card {
            &.sortable-chosen {
              @apply bg-hover-onHover h-10;
              @apply border-0 border-t border-solid;
              @apply border-emphasised-border #{!important};
            }
          }
          .card-layout-board {
            &.sortable-chosen {
              &.list-card-drop {
                border-radius: 0px !important;
                .board-list-card-wrap {
                  height: 40px !important;
                  > * {
                    display: none;
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  .timeline-resize-overlay {
    position: fixed;
    inset: 0;
    z-index: 91;
    cursor: col-resize;
  }

  .timeline-resizer {
    position: absolute;
    top: 0px;
    bottom: 0px;
    width: 8px;
    z-index: 3;

    &::after {
      content: '';
      position: absolute;
      top: 0px;
      bottom: 0px;
      left: 4px;
      width: 1px;
      background-color: transparent;
      box-shadow: transparent;
      transition:
        background-color 0.15s 0.15s,
        box-shadow 0.15s 0.15s;
      pointer-events: none;
    }

    &:hover:not(.mobile *),
    &.is-focused {
      &::after {
        background-color: $resizeHighlight;
        box-shadow: 0 0 0 1px $resizeHighlight;
      }
    }

    &.default-width {
      &.is-focused {
        &::after {
          background-color: $resizeHighlight;
          animation: resizer-pulse 0.5s;
        }
      }
    }
  }
}
</style>
